import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge, type Tone } from '@/components/ui/Badge';
import { SectionCard } from '@/components/ui/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { dashboard } from '@/data';
import type { DashboardTrend, DashboardStatus } from '@/data/types';

interface TrendChartProps { data: DashboardTrend[] }
function TrendChart({ data }: TrendChartProps) {
  const { t } = useTranslation();
  const W = 620, H = 230, padL = 38, padR = 14, padT = 16, padB = 30;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const maxY = 50;
  const sx = (i: number) => padL + (i / (data.length - 1)) * plotW;
  const sy = (v: number) => padT + plotH - (v / maxY) * plotH;
  const line = (key: keyof DashboardTrend) => data.map((d, i) => (i === 0 ? "M" : "L") + sx(i).toFixed(1) + " " + sy(d[key] as number).toFixed(1)).join(" ");
  const area = (key: keyof DashboardTrend) =>
    "M" + sx(0) + " " + sy(0) + data.map((d, i) => "L" + sx(i).toFixed(1) + " " + sy(d[key] as number).toFixed(1)).join(" ") +
    "L" + sx(data.length - 1) + " " + sy(0) + " Z";
  const yticks = [0, 10, 20, 30, 40, 50];

  return (
    <svg viewBox={"0 0 " + W + " " + H} className="mp-chart" role="img" aria-label={t('trendTitle')}>
      <defs>
        <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0054A6" stopOpacity="0.18"></stop>
          <stop offset="100%" stopColor="#0054A6" stopOpacity="0"></stop>
        </linearGradient>
      </defs>
      {yticks.map((v) => (
        <g key={v}>
          <line x1={padL} y1={sy(v)} x2={padL + plotW} y2={sy(v)} stroke="#F1F1F1"></line>
          <text x={padL - 8} y={sy(v) + 3} textAnchor="end" fontSize="10" fill="#9AA5B1">{v}</text>
        </g>
      ))}
      {data.map((d, i) => (
        <text key={i} x={sx(i)} y={H - 12} textAnchor="middle" fontSize="10.5" fill="#707070">{d.m}</text>
      ))}
      <path d={area("cases")} fill="url(#trendFill)"></path>
      <path d={line("cases")} fill="none" stroke="#0054A6" strokeWidth="2.2"></path>
      <path d={line("arrests")} fill="none" stroke="#FFCB05" strokeWidth="2.2"></path>
      {data.map((d, i) => (
        <g key={"p" + i}>
          <circle cx={sx(i)} cy={sy(d.cases)} r="3.2" fill="#fff" stroke="#0054A6" strokeWidth="2"></circle>
          <circle cx={sx(i)} cy={sy(d.arrests)} r="3.2" fill="#fff" stroke="#E0A800" strokeWidth="2"></circle>
        </g>
      ))}
    </svg>
  );
}

interface DonutChartProps { data: DashboardStatus[] }
function DonutChart({ data }: DonutChartProps) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const cx = 70, cy = 70, rMid = 47, sw = 22;
  const C = 2 * Math.PI * rMid;
  const gap = 1.4;
  const segments = data.map((d, i, arr) => {
    const offsetBase = arr.slice(0, i).reduce((s, x) => s + (x.value / total) * C, 0);
    const segLen = (d.value / total) * C;
    const dash = Math.max(segLen - gap, 0);
    return { color: d.color, dash, offset: -offsetBase };
  });
  return (
    <svg viewBox="0 0 140 140" className="mp-donut" role="img">
      <g transform={"rotate(-90 " + cx + " " + cy + ")"}>
        <circle cx={cx} cy={cy} r={rMid} fill="none" stroke="#EFF2F7" strokeWidth={sw}></circle>
        {segments.map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={rMid} fill="none"
            stroke={s.color} strokeWidth={sw}
            strokeDasharray={s.dash + " " + (C - s.dash)}
            strokeDashoffset={s.offset}></circle>
        ))}
      </g>
      <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight="700" fill="#124769">{total}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="9.5" fill="#707070">TOTAL</text>
    </svg>
  );
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const D = dashboard;
  const ln = (ko: string, mn: string) => (lang === "ko" ? ko : mn);
  const unitLabel: Record<string, string> = { cases: t('cases'), persons: t('persons'), kg: "kg" };
  const statusTone: Record<string, Tone> = { stInStorage: "info", stAnalysis: "warning", stDisposalReq: "danger", stTransferred: "neutral" };
  const maxType = Math.max(...D.byType.map((d) => d.g));
  const qa = [
    { icon: "bi-folder-plus", label: t('qaNewCase') },
    { icon: "bi-box-arrow-in-down", label: t('qaNewEvidence') },
    { icon: "bi-flask", label: t('qaRequest') },
    { icon: "bi-file-earmark-text", label: t('qaReport') },
  ];

  return (
    <div className="mp-page" data-screen-label="대시보드">
      <PageHeader
        crumbs={[t('home'), t('mDashboard')]}
        title={t('dashTitle')}
        titleExtra={<div className="mp-pagehead__meta"><span className="mp-pagehead__updated">{t('dashSub')}</span></div>}
        actions={
          <div className="mp-quickactions">
            {qa.map((a, i) => (
              <button key={i} type="button" className="mp-quickbtn">
                <i className={"bi " + a.icon}></i><span>{a.label}</span>
              </button>
            ))}
          </div>
        }
      />

      <div className="mp-kpis">
        {D.kpis.map((k) => (
          <div key={k.key} className={"mp-kpi mp-kpi--" + k.tone}>
            <div className="mp-kpi__icon"><i className={"bi " + k.icon}></i></div>
            <div className="mp-kpi__body">
              <span className="mp-kpi__label">{t(k.key)}</span>
              <span className="mp-kpi__value">{k.value}<small>{unitLabel[k.unit] || k.unit}</small></span>
              <span className={"mp-kpi__delta " + (k.delta >= 0 ? "is-up" : "is-down")}>
                <i className={"bi " + (k.delta >= 0 ? "bi-arrow-up-short" : "bi-arrow-down-short")}></i>
                {Math.abs(k.delta)}% <em>{t('kpiVsPrev')}</em>
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mp-grid mp-grid--dash2">
        <SectionCard
          title={t('trendTitle')}
          extra={
            <div className="mp-chartlegend">
              <span><i className="mp-legdot" style={{ background: "#0054A6" }}></i>{t('trendCases')}</span>
              <span><i className="mp-legdot" style={{ background: "#FFCB05" }}></i>{t('trendArrests')}</span>
            </div>
          }>
          <TrendChart data={D.trend} />
        </SectionCard>
        <SectionCard title={t('statusTitle')}>
          <div className="mp-donutwrap">
            <DonutChart data={D.status} />
            <ul className="mp-donutlegend">
              {D.status.map((s) => (
                <li key={s.key}>
                  <i className="mp-legdot" style={{ background: s.color }}></i>
                  <span>{t(s.key)}</span>
                  <strong>{s.value}</strong>
                </li>
              ))}
            </ul>
          </div>
        </SectionCard>
      </div>

      <div className="mp-grid mp-grid--dash2">
        <SectionCard title={<span>{t('byTypeTitle')} <span className="mp-card__unit">{t('byTypeUnit')}</span></span>}>
          <ul className="mp-barlist">
            {D.byType.map((d) => (
              <li key={d.id}>
                <span className="mp-barlist__label">{ln(d.nameKo, d.name)}</span>
                <span className="mp-barlist__track">
                  <span className="mp-barlist__fill" style={{ width: (d.g / maxType * 100) + "%", background: d.color }}></span>
                </span>
                <span className="mp-barlist__val">{d.g.toLocaleString()}<small>g</small></span>
              </li>
            ))}
          </ul>
        </SectionCard>
        <SectionCard title={t('regionTitle')}>
          <ul className="mp-barlist mp-barlist--region">
            {D.regions.slice().sort((a, b) => b.value - a.value).map((d, i) => {
              const max = Math.max(...D.regions.map((r) => r.value));
              return (
                <li key={i}>
                  <span className="mp-barlist__label">{ln(d.ko, d.mn)}</span>
                  <span className="mp-barlist__track">
                    <span className="mp-barlist__fill" style={{ width: (d.value / max * 100) + "%", background: i === 0 ? "#0054A6" : "#5B8BC4" }}></span>
                  </span>
                  <span className="mp-barlist__val">{d.value}<small>{t('cases')}</small></span>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      </div>

      <div className="mp-grid mp-grid--dash-recent">
        <SectionCard
          title={t('recentTitle')}
          extra={<button type="button" className="mp-tablelink" onClick={() => navigate('/case')}>{t('viewAll')}<i className="bi bi-chevron-right"></i></button>}
          flush>
          <table className="mp-table">
            <thead>
              <tr>
                <th>{t('caseNo')}</th>
                <th>{t('caseName')}</th>
                <th>{t('leadInvestigator')}</th>
                <th>{t('receivedDate')}</th>
                <th>{t('status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {D.recent.map((c) => (
                <tr key={c.no} style={{ cursor: "pointer" }} onClick={() => navigate('/case')}>
                  <td><span className="mp-caseno">{c.no}</span></td>
                  <td className="mp-table__strong">{ln(c.ko, c.mn)}</td>
                  <td>{c.lead}</td>
                  <td>{c.date}</td>
                  <td><Badge tone={statusTone[c.status]}>{t(c.status)}</Badge></td>
                  <td><i className="bi bi-chevron-right" style={{ color: "var(--fg-muted)" }}></i></td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
        <SectionCard title={t('alertsTitle')} flush>
          <ul className="mp-alertlist">
            {D.alerts.map((a, i) => (
              <li key={i} className={"mp-alertlist__item is-" + a.tone}>
                <i className={"bi " + a.icon}></i>
                <span>{ln(a.ko, a.mn)}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>
    </div>
  );
}
